import { Button, Heading, Text } from "@instacover/ui"
import { createFileRoute, Link } from "@tanstack/react-router"
import type { PropsWithChildren } from "react"

import { BaseLayout } from "@/components/layouts/BaseLayout"
import { IconArrowLeft } from "@/images/xd/icons/IconArrowLeft"
import { LINKS } from "@/routing/links"

export const Route = createFileRoute("/$sessionId/_session/privacy-policy")({
  component: PrivacyPolicyPage,
})

const LALUX_PRIVACY_POLICY_URL = "https://www.lalux.lu/fr/protection-des-donnees"

function PrivacyPolicyPage() {
  const { sessionId } = Route.useParams()

  return (
    <BaseLayout hideCounter hideLocales dataTestId="privacy-policy">
      <div className="h-vh-header mx-auto max-w-2xl overflow-y-auto px-6 pt-6 pb-10">
        <Heading as="h1" variant="h1">
          InstaCover.ai Product Privacy Policy
        </Heading>

        <Text as="p" className="mt-4">
          As part of your application for an insurance contract, you are invited to use our platform for photographing
          and automated analysis of vehicle damages. The processing of your personal data is governed by the General
          Data Protection Regulation (GDPR), and this statement aims to provide you with transparent information.
        </Text>

        <Section title="1. Data controller">
          <Text as="p">
            “LA LUXEMBOURGEOISE Société Anonyme d’Assurances” with which you are applying for a contract is the data
            controller. Our company acts as a processor, providing the technical image analysis platform on its behalf.
          </Text>
        </Section>

        <Section title="2. Data collected and processed">
          <Text as="p">The following data may be collected and processed:</Text>

          <List>
            <Text as="li">Photographs of the vehicle;</Text>
            <Text as="li">License plate number;</Text>
            <Text as="li">Vehicle Identification Number (VIN);</Text>
            <Text as="li">AI analysis results: confidence level and damage assessment;</Text>
            <Text as="li">Photo metadata: date/time, GPS coordinates (if available), device type, browser type.</Text>
          </List>
        </Section>

        <Section title="3. Purposes and legal basis">
          <Text as="p">Your data are processed in order to:</Text>

          <List>
            <Text as="li">Allow automated photo analysis to detect and assess possible damages;</Text>
            <Text as="li">Facilitate the subscription and management of your future insurance contract.</Text>
          </List>

          <Text as="p" className="mt-2">
            The legal basis is the performance of pre-contractual measures (Art. 6.1.b GDPR).
          </Text>
        </Section>

        <Section title="4. No fully automated decision-making">
          <Text as="p">
            AI results are used solely as decision support. The final decision is always taken by an employee of the
            insurance company.
          </Text>
        </Section>

        <Section title="5. Data retention">
          <List>
            <Text as="li">If the subscription is successful: data are retained for the duration of the contract;</Text>
            <Text as="li">If unsuccessful: data are retained for a maximum of 3 years.</Text>
          </List>
        </Section>

        <Section title="6. Recipients and sub-processors">
          <Text as="p">
            Your data are transmitted to the insurance company. We use NeonDB (database) and Sentry (monitoring).
            Initial data are stored in the EU. For the proper functioning of their services, some data may be
            transferred to the United States. These transfers are governed by the European Commission’s Standard
            Contractual Clauses.
          </Text>
        </Section>

        <Section title="7. Data security">
          <Text as="p">
            We implement appropriate technical and organizational measures such as encryption, access control and
            regular monitoring to protect your data.
          </Text>
        </Section>

        <Section title="8. Use for AI improvement">
          <Text as="p">
            Some data may be irreversibly anonymized and used by our company as an independent controller for research
            and algorithm improvement purposes.
          </Text>
        </Section>

        <Section title="9. Transfers outside the EU">
          <Text as="p">
            Other than the cases mentioned with NeonDB and Sentry, your data are not transferred to third countries.
          </Text>
        </Section>

        <Section title="10. Your rights">
          <Text as="p">
            You have the rights of access, rectification, erasure, restriction, objection and portability. These rights
            can be exercised with your insurance company.
          </Text>
        </Section>

        <Section title="11. Further information">
          <Text as="p">
            For more details, please refer to your insurance company’s Privacy Policy available on its website (
            <a href={LALUX_PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" className="text-ic-primary underline">
              {LALUX_PRIVACY_POLICY_URL}
            </a>
            )
          </Text>
        </Section>

        <div className="mt-10 flex justify-center">
          <Link to={LINKS.consent(sessionId)}>
            <Button
              as="span"
              variant="primary"
              shape="wide"
              data-testid="privacy-policy-back-btn"
              icon={<IconArrowLeft className="size-6" />}
            >
              Back
            </Button>
          </Link>
        </div>
      </div>
    </BaseLayout>
  )
}

function Section(props: PropsWithChildren<{ title: string }>) {
  const { title, children } = props

  return (
    <section className="mt-6">
      <Heading as="h2" variant="h2" className="mb-2">
        {title}
      </Heading>

      {children}
    </section>
  )
}

function List(props: PropsWithChildren) {
  const { children } = props

  return <ul className="mt-2 list-disc pl-5">{children}</ul>
}
